import { useEffect, useState } from "react"

/**
 * `useDebounce` — return a value that lags the input by `delayMs`.
 *
 * Used by the candidate list search input (OKS-17 §Search debounce
 * 300ms). Pattern: caller controls `value` from input change; the
 * returned `debounced` only updates `delayMs` after the last change.
 * The Inertia `router.get` call is then made off `debounced` (not
 * `value`) so each keystroke doesn't cost a round-trip.
 *
 * SSR-safe: works without `window` because it doesn't subscribe to
 * any browser API.
 *
 * @example
 * ```ts
 * const [q, setQ] = useState("")
 * const debouncedQ = useDebounce(q, 300)
 * useEffect(() => {
 *   router.get(candidatesPath(), { q: debouncedQ || undefined }, ...)
 * }, [debouncedQ])
 * ```
 */
export function useDebounce<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(id)
  }, [value, delayMs])

  return debounced
}
